import React from 'react';

import merge from 'lodash/merge';

import { ArticlePicker } from '@lumapps/article-picker/components/ArticlePickerDialog';
import { ArticlePickerFilterIds, CommunityNavArticle } from '@lumapps/articles/types';
import { selectSpaceOrCommunityId } from '@lumapps/communities/ducks/selectors';
import { Community } from '@lumapps/communities/types';
import { CommunityPickerDialog } from '@lumapps/community-pickers/components/CommunityPickerDialog';
import { useCommunityListPicker } from '@lumapps/community-pickers/hooks/useCommunityListPicker';
import { ContentListPicker } from '@lumapps/content-list-picker/components/ContentListPicker';
import { ContentPicker } from '@lumapps/content-picker/components/ContentPicker';
import { ContentFiltersProps } from '@lumapps/contents/components/ContentFilters';
import { Content } from '@lumapps/contents/types';
import { DirectoryPicker } from '@lumapps/directory-picker/components/DirectoryPicker';
import { getFeedById } from '@lumapps/groups/ducks/selectors';
import { getCurrentInstance } from '@lumapps/instance/ducks/selectors';
import { NavigationItemType, NavigationMapItem, NavigationMapPageReferenceType } from '@lumapps/navigation/types';
import { useSelector } from '@lumapps/redux/react';
import { useTranslate } from '@lumapps/translations';
import { UserDirectoryPicker } from '@lumapps/user-directory-picker/components/UserDirectoryPicker';

import { NavigationItemModal as Modal } from '.';
import { PickerTypes } from '../../constants';
import { useNavigationAdminConfiguration } from '../../context/NavigationAdminConfigurationContext';
import { useNavigationAdminVisibleByReferenceDispatch } from '../../context/NavigationAdminVisibleByReferenceContext';
import { useConvertContentToNavigationNode } from '../../hooks/useConvertEntityToNavigationNode';
import { NAVIGATION_ADMIN } from '../../keys';
import { NavigationItemModal } from '../../types';

export interface PickerNavigationItemModalProps extends NavigationItemModal {
    isOpen?: boolean;
    isNavigationItemModalOpen?: boolean;
    /** The type of picker to use. If undefined, will use the page reference type. */
    pickerType?: PickerTypes;
}

export const PickerNavigationItemModal: React.FC<PickerNavigationItemModalProps> = ({
    languages,
    defaultLanguage,
    onSave,
    navigationItem,
    isOpen = false,
    isNavigationItemModalOpen = false,
    onClose,
    buttonRef,
    isEdit,
    scope,
    navigationItemModalProps = {},
    pickerType,
}) => {
    const { translateKey } = useTranslate();
    const [isPickerOpen, setIsPickerOpen] = React.useState(isOpen);

    const convertEntity = useConvertContentToNavigationNode({ languages, defaultLanguage });

    // The currentNavigationItem is either the Navigation Item being edited, or else a new navigation item with new resource
    const [currentNavigationItem, setCurrentNavigationItem] = React.useState<NavigationMapItem>(navigationItem);

    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(isNavigationItemModalOpen);
    const { availableEditableNodeFields, fetchAtInstanceLevel } = useNavigationAdminConfiguration();

    const { id: currentInstanceId } = useSelector(getCurrentInstance);
    const spaceOrCommunityId = useSelector(selectSpaceOrCommunityId);
    const feeds = useSelector(getFeedById);

    const addVisibleByGroupReferences = useNavigationAdminVisibleByReferenceDispatch();

    const shouldDisplayNavigationItemModal = isModalOpen && Boolean(currentNavigationItem);

    // This picker is only used for nodes where pageReference is set, so pageType cannot be undefined
    const pageType = navigationItem.pageReference?.type as NavigationMapPageReferenceType;
    const displayedPicker = pickerType || pageType;
    const canSetVisibility = Boolean(
        navigationItemModalProps.displayedFields?.visibility || availableEditableNodeFields?.visibility,
    );

    // Depending on whether we fetchAtInstanceLevel or not, add the currentInstanceId to the pickers fetchParams
    const fetchParamsOverrides = React.useMemo(
        () => (fetchAtInstanceLevel ? { instanceId: currentInstanceId } : undefined),
        [currentInstanceId, fetchAtInstanceLevel],
    );

    // enabledFilters of the content picker
    const contentEnabledFilters = React.useMemo<ContentFiltersProps['enabledFilters']>(() => {
        // If we fetch at customer level, use the enabledFilters by default
        if (!fetchAtInstanceLevel) {
            return undefined;
        }

        switch (displayedPicker) {
            case NavigationItemType.CONTENT:
                return ['custom-content-type', 'status', 'author'];
            case NavigationItemType.CUSTOM_LIST:
                return ['custom-content-type', 'author'];
            case NavigationItemType.DIRECTORY:
            case NavigationItemType.USER_DIRECTORY:
                return ['author'];
            default:
                return undefined;
        }
    }, [displayedPicker, fetchAtInstanceLevel]);

    const commonPickerProps = {
        fetchParamsOverrides,
        isOpen: isPickerOpen,
        onCancel: () => {
            setIsPickerOpen(false);
        },
        onConfirm: ([c]: Community[] | Content[] | CommunityNavArticle[]) => {
            const convertedEntity = convertEntity({
                entity: c,
                type: pageType,
            });
            const confirmNavigationItem = merge(convertedEntity, navigationItem);

            // add the resource's visibility in the dict. The info come from Redux
            addVisibleByGroupReferences(
                confirmNavigationItem.pageReference?.groupIds?.reduce(
                    (acc, groupId) =>
                        feeds && feeds[groupId]
                            ? {
                                  ...acc,
                                  [groupId]: feeds[groupId]?.name,
                              }
                            : acc,
                    {},
                ),
            );

            if (canSetVisibility) {
                setIsModalOpen(true);
                setIsPickerOpen(false);
                setCurrentNavigationItem(confirmNavigationItem);
            } else {
                onSave(confirmNavigationItem);
            }
        },
        onVisibilityChange: (open: boolean) => {
            if (!open && !isModalOpen) {
                onClose();
            }
        },
        parentElement: buttonRef,
    };

    return (
        <>
            {displayedPicker === NavigationItemType.COMMUNITY && (
                <CommunityPickerDialog
                    {...commonPickerProps}
                    fetchParams={commonPickerProps.fetchParamsOverrides}
                    communityPickerHook={useCommunityListPicker}
                />
            )}
            {displayedPicker === NavigationItemType.CONTENT && (
                <ContentPicker {...commonPickerProps} enabledFilters={contentEnabledFilters} />
            )}
            {displayedPicker === NavigationItemType.CUSTOM_LIST && (
                <ContentListPicker {...commonPickerProps} enabledFilters={contentEnabledFilters} />
            )}
            {displayedPicker === NavigationItemType.ARTICLE && (
                <ArticlePicker
                    {...commonPickerProps}
                    context="community"
                    onlyUseCurrentInstance={fetchAtInstanceLevel}
                    enabledFilters={
                        // Return empty array to hide the filters button on space or community
                        spaceOrCommunityId ? [] : [ArticlePickerFilterIds.communities]
                    }
                />
            )}
            {displayedPicker === NavigationItemType.DIRECTORY && (
                <DirectoryPicker {...commonPickerProps} enabledFilters={contentEnabledFilters} />
            )}
            {displayedPicker === NavigationItemType.USER_DIRECTORY && (
                <UserDirectoryPicker {...commonPickerProps} enabledFilters={contentEnabledFilters} />
            )}
            {shouldDisplayNavigationItemModal ? (
                <Modal
                    isOpen={shouldDisplayNavigationItemModal}
                    languages={languages}
                    defaultLanguage={defaultLanguage}
                    message={translateKey(
                        displayedPicker === NavigationItemType.COMMUNITY
                            ? NAVIGATION_ADMIN.COMMUNITY_DESCRIPTION
                            : NAVIGATION_ADMIN.CONTENT_DESCRIPTION,
                    )}
                    onSave={onSave}
                    buttonRef={buttonRef}
                    scope={scope}
                    navigationItem={currentNavigationItem}
                    onClose={onClose}
                    isEdit={isEdit}
                    titlePlaceholder={translateKey(NAVIGATION_ADMIN.NAVIGATION_ITEM_TITLE)}
                    {...navigationItemModalProps}
                    displayedFields={{
                        ...navigationItemModalProps?.displayedFields,
                        url: false,
                        title: false,
                    }}
                    pickerType={pickerType}
                />
            ) : null}
        </>
    );
};
